/**
 * Org list — table with search, sort, status badges, privacy posture badge.
 */
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import SigninLinkPanel from '../../components/ops_admin/SigninLinkPanel';

const POSTURE_BADGE = {
  max_cj: { label: 'Max+CJ', cls: 'posture-max-cj' },
  max: { label: 'Max', cls: 'posture-max' },
  names: { label: 'Names', cls: 'posture-names' },
  full: { label: 'Full', cls: 'posture-full' },
};

const STATE_BADGE = {
  active: 'badge-active',
  suspended: 'badge-suspended',
  purged: 'badge-purged',
};

function PostureBadge({ posture }) {
  const b = POSTURE_BADGE[posture] || { label: posture, cls: '' };
  return <span className={`posture-badge ${b.cls}`}>{b.label}</span>;
}

export default function OrgList({ opsRole }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (q) params.set('q', q);
      if (stateFilter) params.set('state', stateFilter);
      const data = await api.get(`/ops_admin/orgs?${params}`);
      setOrgs(data.orgs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [q, stateFilter]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const canCreate = ['admin', 'owner'].includes(opsRole);

  return (
    <div className="ops-org-list">
      <div className="ops-page-header">
        <h1>Organizations</h1>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + New Org
          </button>
        )}
      </div>

      <div className="ops-filters">
        <input
          className="form-input search-input"
          placeholder="Search name or slug..."
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <select
          className="form-input"
          value={stateFilter}
          onChange={e => setStateFilter(e.target.value)}
        >
          <option value="">All states</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="purged">Purged</option>
        </select>
        <button className="btn btn-sm btn-ghost" onClick={fetchOrgs}>Refresh</button>
      </div>

      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <p className="loading-text">Loading...</p>
      ) : (
        <table className="ops-table">
          <thead>
            <tr>
              <th>Name / Slug</th>
              <th>State</th>
              <th>Privacy</th>
              <th>Type</th>
              <th>Members</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orgs.length === 0 && (
              <tr><td colSpan={7} className="empty-row">No organizations found</td></tr>
            )}
            {orgs.map(org => (
              <tr key={org.id}>
                <td>
                  <div className="org-name">
                    {org.is_criminal_justice
                      ? <span title="Criminal justice org">{org.id.slice(0, 8)}</span>
                      : org.name}
                  </div>
                  <div className="org-slug">{org.slug}</div>
                </td>
                <td>
                  <span className={`badge ${STATE_BADGE[org.state] || ''}`}>{org.state}</span>
                  {!org.first_login_at && <span className="badge badge-pending">pending</span>}
                </td>
                <td><PostureBadge posture={org.privacy_posture} /></td>
                <td>{org.org_type}</td>
                <td>{org.member_count}</td>
                <td>{org.created_at ? new Date(org.created_at).toLocaleDateString() : '—'}</td>
                <td>
                  <Link to={`/ops_admin/orgs/${org.id}`} className="btn btn-sm btn-ghost">
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && (
        <CreateOrgModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchOrgs(); }} />
      )}
    </div>
  );
}

function CreateOrgModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', admin_email: '', admin_display_name: '', org_type: 'other', plan_template: 'passthrough',
    is_criminal_justice: false, delivery: 'white_glove', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await api.post('/ops_admin/orgs', form);
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (result?.org) {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <h2>Org created — {result.org?.name}</h2>
          <SigninLinkPanel
            email={result.admin_email}
            setupLink={result.setup_link}
            firebaseUserCreated={result.firebase_user_created}
            hasPassword={result.has_password}
            identityEmail={result.identity_email}
            authProvider={result.auth_provider}
            emailSent={result.delivery === 'invite_email' && result.setup_link ? !!result.email_sent : undefined}
            onDone={() => { setResult(null); onCreated(); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>Create Organization</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Org Name *</label>
            <input className="form-input" required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">Admin Email *</label>
            <input className="form-input" type="email" required value={form.admin_email} onChange={e => setForm(f => ({...f, admin_email: e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">Admin Name *</label>
            <input className="form-input" required placeholder="Jane Doe" value={form.admin_display_name} onChange={e => setForm(f => ({...f, admin_display_name: e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">Org Type</label>
            <select className="form-input" value={form.org_type} onChange={e => setForm(f => ({...f, org_type: e.target.value}))}>
              <option value="other">Other</option>
              <option value="da_office">DA Office</option>
              <option value="public_defender">Public Defender</option>
              <option value="law_firm">Law Firm</option>
              <option value="government">Government</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Plan Template</label>
            <select className="form-input" value={form.plan_template} onChange={e => setForm(f => ({...f, plan_template: e.target.value}))}>
              <option value="passthrough">Pass-through</option>
              <option value="allotment">Allotment</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Delivery Mode</label>
            <select className="form-input" value={form.delivery} onChange={e => setForm(f => ({...f, delivery: e.target.value}))}>
              <option value="white_glove">White glove (one-time link)</option>
              <option value="invite_email">Invite email</option>
            </select>
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input type="checkbox" checked={form.is_criminal_justice} onChange={e => setForm(f => ({...f, is_criminal_justice: e.target.checked}))} />
              {' '}Criminal justice org (max privacy, locked)
            </label>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
          </div>
          {error && <p className="error-text">{error}</p>}
          <div className="button-row">
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create'}</button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
